import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Copy, X } from "lucide-react";
import { toast } from "sonner";

export default function ProtocolsList() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [protocols, setProtocols] = useState([]);
  const [sites, setSites] = useState([]);
  const [types, setTypes] = useState([]);
  const [filters, setFilters] = useState({
    site_id: searchParams.get("site_id") || "",
    type_operation_id: searchParams.get("type_operation_id") || "",
    statut: searchParams.get("statut") || "",
    search: "",
    echeance: searchParams.get("echeance") || "",
  });

  const load = useCallback(() => {
    const params = {};
    if (filters.site_id) params.site_id = filters.site_id;
    if (filters.type_operation_id) params.type_operation_id = filters.type_operation_id;
    if (filters.statut) params.statut = filters.statut;
    if (filters.search) params.search = filters.search;
    if (filters.echeance) params.echeance_jours = filters.echeance;
    api.get("/protocoles", { params }).then((res) => setProtocols(res.data));
  }, [filters]);

  useEffect(() => {
    api.get("/sites").then((res) => setSites(res.data));
    api.get("/types-operations").then((res) => setTypes(res.data));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clearEcheanceFilter = () => {
    setFilters({ ...filters, echeance: "" });
    searchParams.delete("echeance");
    setSearchParams(searchParams);
  };

  const duplicate = async (id) => {
    await api.post(`/protocoles/${id}/dupliquer`);
    toast.success(t("common.duplicate"));
    load();
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="protocols-list-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{t("nav.protocols")}</h1>
        <Link to="/protocoles/nouveau">
          <Button data-testid="btn-create-protocol">
            <Plus className="w-4 h-4" />
            {t("nav.create_protocol")}
          </Button>
        </Link>
      </div>

      <div className="flex flex-wrap gap-3">
        <Input
          data-testid="protocol-search-input"
          placeholder={t("common.search")}
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="max-w-xs"
        />
        <Select value={filters.site_id || "all"} onValueChange={(v) => setFilters({ ...filters, site_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-site-select" className="w-48">
            <SelectValue placeholder={t("common.all_sites")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all_sites")}</SelectItem>
            {sites.map((s) => (
              <SelectItem key={s.Site_ID} value={s.Site_ID}>
                {s.Nom}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.type_operation_id || "all"} onValueChange={(v) => setFilters({ ...filters, type_operation_id: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-operation-select" className="w-56">
            <SelectValue placeholder={t("common.all_operations")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all_operations")}</SelectItem>
            {types.map((o) => (
              <SelectItem key={o.Type_ID} value={o.Type_ID}>
                {o.Nom}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.statut || "all"} onValueChange={(v) => setFilters({ ...filters, statut: v === "all" ? "" : v })}>
          <SelectTrigger data-testid="filter-status-select" className="w-44">
            <SelectValue placeholder={t("common.all_statuses")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all_statuses")}</SelectItem>
            {["BROUILLON", "A_VALIDER", "VALIDE", "PUBLIE", "OBSOLETE"].map((s) => (
              <SelectItem key={s} value={s}>
                {t(`statuses.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {filters.echeance && (
        <div className="flex items-center gap-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-800 rounded-lg px-3 py-2 w-fit" data-testid="echeance-filter-chip">
          {t("common.due_date")} &lt; {filters.echeance}j
          <button onClick={clearEcheanceFilter} data-testid="btn-clear-echeance-filter">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        {protocols.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-500" data-testid="protocols-empty-state">
            {t("common.no_results")}
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">ID</th>
                <th className="px-4 py-3">{t("common.title")}</th>
                <th className="px-4 py-3">{t("common.site")}</th>
                <th className="px-4 py-3">{t("common.version")}</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {protocols.map((p) => (
                <tr
                  key={p.Protocol_ID}
                  className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/40"
                  data-testid={`protocol-row-${p.Protocol_ID}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">
                    <Link to={`/protocole/${p.Protocol_ID}`} className="hover:underline text-amber-600 dark:text-amber-400">
                      {p.Protocol_ID}
                    </Link>
                  </td>
                  <td className="px-4 py-3 font-medium text-slate-800 dark:text-slate-200">{p.Titre}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{p.Site_Nom}</td>
                  <td className="px-4 py-3 font-mono">{p.Version_Courante}</td>
                  <td className="px-4 py-3">
                    <StatusBadge status={p.Statut} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button variant="ghost" size="icon" data-testid={`btn-duplicate-${p.Protocol_ID}`} onClick={() => duplicate(p.Protocol_ID)}>
                      <Copy className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
